import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import dayjs from 'dayjs';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Box,
  Grid,
  Stack,
  Link,
  Divider,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ImageIcon from '@mui/icons-material/Image';
import TitleIcon from '@mui/icons-material/Title';
import { FormProvider, RHFTextField } from '../../../components/hook-form';
import { useApi } from '../../../hooks/useApi';
import useNotification from '../../../hooks/useNotificaction';
import ImageUploaderWithPreview from '../../../components/hook-form/ImageUploaderWithPreview';

// ----------------------------------------------------------------------

export default function AnnouncementForm({ announcement }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { doSave } = useApi();
  const [sendNotification] = useNotification();

  const ModelSchema = Yup.object().shape({
    title: Yup.string().required(t('Required')),
    body: Yup.string().nullable(),
    active: Yup.boolean(),
    expires_at: Yup.mixed().nullable(),
    image: !announcement
      ? Yup.mixed().required(t('image_required'))
      : Yup.mixed().nullable(),
  });

  const MAX_BODY_LENGTH = 5000;

  const defaultValues = {
    title: '',
    body: '',
    active: true,
    expires_at: null,
  };

  const methods = useForm({
    resolver: yupResolver(ModelSchema),
    defaultValues,
  });

  const { handleSubmit, setValue, control, watch, formState: { isSubmitting } } = methods;
  const activeValue = watch('active');
  const expiresAt = watch('expires_at');
  const bodyValue = watch('body') || '';

  useEffect(() => {
    if (announcement) {
      setValue('title', announcement.title || '');
      setValue('body', announcement.body || '');
      setValue('active', !!announcement.active);
      setValue('expires_at', announcement.expires_at ? dayjs(announcement.expires_at) : null);
    }
  }, [announcement]);

  const onInvalidSubmit = (errors) => {
    const firstError = Object.values(errors)[0];
    sendNotification({ msg: firstError?.message || t('something_went_wrong'), variant: 'error' });
  };

  const onSubmit = async (data) => {
    if (data.image instanceof File || data.image instanceof Blob) {
      const compressedFile = await imageCompression(data.image, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      });
      setValue('image', compressedFile);
      data.image = compressedFile;
    } else {
      data.image = null;
    }

    if (data.expires_at && dayjs(data.expires_at).isValid()) {
      data.expires_at = dayjs(data.expires_at).format('YYYY-MM-DD HH:mm:ss');
    } else {
      data.expires_at = '';
    }

    const headers = { headers: { 'Content-Type': 'multipart/form-data' } };
    const result = await doSave('announcements', data, t('announcement_saved'), '', announcement, headers);
    if (result) navigate('/dashboard/announcement/');
  };

  const isExpired = expiresAt && dayjs.isDayjs(expiresAt) && expiresAt.isBefore(dayjs());

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}>
      <Grid container spacing={4}>
        {/* LEFT: Main content */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <TitleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" color="text.secondary">{t('title')}</Typography>
              </Stack>
              <RHFTextField name="title" label={t('title')} fullWidth />
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <TitleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" color="text.secondary">{t('description')}</Typography>
              </Stack>
              <RHFTextField
                name="body"
                label={t('description')}
                fullWidth
                multiline
                minRows={5}
                inputProps={{ maxLength: MAX_BODY_LENGTH }}
              />
              <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
                <Typography
                  variant="caption"
                  sx={{
                    color: bodyValue.length > MAX_BODY_LENGTH * 0.9 ? 'error.main' : 'text.disabled',
                    fontWeight: bodyValue.length > MAX_BODY_LENGTH * 0.9 ? 600 : 400,
                  }}
                >
                  {bodyValue.length} / {MAX_BODY_LENGTH}
                </Typography>
                {bodyValue.length > MAX_BODY_LENGTH * 0.9 && (
                  <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700 }}>
                    ⚠️ {t('max_length_exceeded') || 'Máximo excedido'}
                  </Typography>
                )}
              </Stack>
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <ImageIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" color="text.secondary">{t('image')}</Typography>
              </Stack>

              {/* Image recommendation guide */}
              <Box
                sx={{
                  bgcolor: '#f0f6ff',
                  border: '1px solid',
                  borderColor: '#d0e2ff',
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: '#032C65',
                        color: '#fff',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 14,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      i
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#032C65' }}>
                      {t('image_recommendations_title') || 'Recomendaciones de imagen'}
                    </Typography>
                  </Stack>

                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      📐 {t('recommended_dimensions') || 'Dimensiones recomendadas'}: <strong>1600 x 900 px</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ↔ {t('aspect_ratio') || 'Proporción (aspect ratio)'}: <strong>16:9</strong> ({t('horizontal_widescreen') || 'horizontal / widescreen'})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      🖼 {t('format') || 'Formato'}: <strong>JPG</strong> {t('or') || 'o'} <strong>PNG</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ⚖️ {t('max_weight') || 'Peso máximo sugerido'}: <strong>2 MB</strong>
                    </Typography>
                  </Stack>

                  {/* SVG comparison graphic */}
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    sx={{ py: 1 }}
                  >
                    {/* Correct: 16:9 horizontal */}
                    <Stack alignItems="center" spacing={0.5}>
                      <Box
                        sx={{
                          width: 80,
                          height: 45,
                          bgcolor: '#e8f5e9',
                          border: '2px solid',
                          borderColor: '#4caf50',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                        }}
                      >
                        <Typography variant="caption" fontWeight={700} sx={{ color: '#2e7d32', fontSize: 10 }}>
                          ✓
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600, fontSize: 10, textAlign: 'center' }}>
                        {t('recommended_label') || 'Recomendado 1600×900'}
                      </Typography>
                    </Stack>

                    {/* Incorrect: vertical/portrait */}
                    <Stack alignItems="center" spacing={0.5}>
                      <Box
                        sx={{
                          width: 45,
                          height: 80,
                          bgcolor: '#fff3e0',
                          border: '2px solid',
                          borderColor: '#ff9800',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="caption" fontWeight={700} sx={{ color: '#e65100', fontSize: 10 }}>
                          ✗
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600, fontSize: 10, textAlign: 'center', maxWidth: 80 }}>
                        {t('avoid_vertical_label') || 'Evitar verticales o cuadradas'}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Typography variant="caption" sx={{ color: '#e65100', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    ⚠️ {t('image_orientation_warning') || 'Las imágenes verticales o cuadradas se mostrarán recortadas. Use imágenes horizontales para mejor resultado en web y móvil.'}
                  </Typography>
                </Stack>
              </Box>

              <ImageUploaderWithPreview name="image" currentUrl={announcement?.picture || ''} />
            </Box>
          </Stack>
        </Grid>

        {/* RIGHT: Settings panel */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 3,
              background: '#f8faff',
              position: 'sticky',
              top: 80,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              {t('settings') || 'Configuración'}
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {/* Active toggle */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
              <Box>
                <Typography variant="body2" fontWeight={600}>{t('active') || 'Activo'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {activeValue
                    ? (t('visible_on_site') || 'Visible en el sitio')
                    : (t('hidden_from_site') || 'Oculto del sitio')}
                </Typography>
              </Box>
              <Controller
                name="active"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    color="primary"
                  />
                )}
              />
            </Stack>

            {/* Expiry date */}
            <Box mb={3}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <CalendarMonthIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                <Typography variant="body2" fontWeight={600}>
                  {t('expiry_date') || 'Fecha de expiración'}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                {t('expiry_date_hint') || 'El anuncio se desactivará automáticamente en esta fecha.'}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Controller
                  name="expires_at"
                  control={control}
                  render={({ field }) => (
                    <MobileDateTimePicker
                      label={t('expiry_date') || 'Fecha de expiración'}
                      value={field.value || null}
                      onChange={field.onChange}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true },
                        actionBar: { actions: ['clear', 'accept'] },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>

              {isExpired && (
                <Chip
                  label={t('expired') || 'Expirado'}
                  color="error"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
              {expiresAt && !isExpired && (
                <Chip
                  label={`${t('expires') || 'Expira'}: ${dayjs(expiresAt).format('MMM D, YYYY h:mm A')}`}
                  color="warning"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Stack spacing={1.5}>
              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
              >
                {t('save')}
              </LoadingButton>
              <Link
                component={RouterLink}
                variant="body2"
                underline="hover"
                to="/dashboard/announcement/"
                sx={{ textAlign: 'center', display: 'block', color: 'text.secondary' }}
              >
                {t('go_back')}
              </Link>
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
}

